import React from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useParams } from "react-router-dom";
import BookingAccessAdmin from "@/pages/admin/BookingAccessAdmin";
import BookerActivityReport from "@/pages/admin/BookerActivityReport";
import RecruiterActivityReconciliation from "@/pages/admin/RecruiterActivityReconciliation";
import ActivityReport from "@/pages/activity/ActivityReport";
import ActivityRecords from "@/pages/activity/ActivityRecords";
import ActivityReportSettings from "@/pages/admin/ActivityReportSettings";
import MetricList from "@/pages/reports/MetricList";
import "@/App.css";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { AppShell } from "@/components/layout/AppShell";
import ScrollToTop from "@/components/common/ScrollToTop";
import HelpCenter from "@/pages/help/HelpCenter";
import HelpArticle from "@/pages/help/HelpArticle";
import MyTickets from "@/pages/support/MyTickets";
import SupportTicketsQueue from "@/pages/support/SupportTicketsQueue";
import HelpManager from "@/pages/admin/HelpManager";
import { PageLoader } from "@/components/common/Loading";
import { AccessDenied } from "@/components/common/AccessDenied";
import KnowledgeCenter from "@/pages/admin/KnowledgeCenter";
import KnowledgeImport from "@/pages/admin/KnowledgeImport";
import KnowledgeLibrary from "@/pages/admin/KnowledgeLibrary";
import ProgramPage from "@/pages/programs/ProgramPage";
import MyTeam from "@/pages/team/MyTeam";
import MyProteges from "@/pages/team/MyProteges";
import AgentHome from "@/pages/agent/AgentHome";
import ContentLibrary from "@/pages/agent/ContentLibrary";
import ContentReader from "@/pages/agent/ContentReader";
import TrainingLibrary from "@/pages/training/TrainingLibrary";
import TrainingItem from "@/pages/training/TrainingItem";
import AgentContentAdmin from "@/pages/admin/AgentContentAdmin";
import EventsTrainingPage from "@/pages/events/EventsTraining";
import EventsManagement from "@/pages/events/EventsManagement";
import SupportTeamEditor from "@/pages/admin/SupportTeamEditor";
import { Toaster } from "@/components/ui/sonner";

import Login from "@/pages/auth/Login";
import ForgotPassword from "@/pages/auth/ForgotPassword";
import ResetPassword from "@/pages/auth/ResetPassword";
import AcceptInvitation from "@/pages/auth/AcceptInvitation";
import VerifyEmail from "@/pages/auth/VerifyEmail";
import Home from "@/pages/Home";
import Security from "@/pages/Security";
import PeopleList from "@/pages/people/PeopleList";
import AgentMyTeam from "@/pages/agent/MyTeam";
import AgencyLeads from "@/pages/agent/AgencyLeads";
import AgencyHiring from "@/pages/agent/AgencyHiring";
import MyRoadmapPage from "@/components/roadmap/RetailRoadmap";
import IdeaShare from "@/pages/ideas/IdeaShare";
import Directory from "@/pages/people/Directory";
import NewLeads from "@/pages/recruiting/NewLeads";
import JobLanding from "@/pages/public/JobLanding";
import ApplicantPortal from "@/pages/applicant/ApplicantPortal";
import PersonDetail from "@/pages/people/PersonDetail";
import DeletedPeople from "@/pages/people/DeletedPeople";
import Pipeline from "@/pages/recruiting/Pipeline";
import Recruiting from "@/pages/recruiting/Recruiting";
import MyReferrals from "@/pages/recruiting/MyReferrals";
import MyDashboard from "@/pages/recruiting/MyDashboard";
import ReferralDashboard from "@/pages/referrals/ReferralDashboard";
import AddLead from "@/pages/referrals/AddLead";
import MyReferralsList from "@/pages/referrals/MyReferralsList";
import ReferralDetail from "@/pages/referrals/ReferralDetail";
import InviteCandidate from "@/pages/referrals/InviteCandidate";
import { MyRecruitingActivity, PartnerKnowledge } from "@/pages/referrals/PartnerPages";
import OpportunityDetail from "@/pages/recruiting/OpportunityDetail";
import Tasks from "@/pages/tasks/Tasks";
import TaskDetail from "@/pages/tasks/TaskDetail";
import Projects from "@/pages/projects/Projects";
import ProjectDetail from "@/pages/projects/ProjectDetail";
import Calendar from "@/pages/calendar/Calendar";
import SchedulingPages from "@/pages/calendar/SchedulingPages";
import BookMeeting from "@/pages/calendar/BookMeeting";
import DocumentMeeting from "@/pages/calendar/DocumentMeeting";
import SchedulingPageEditor from "@/pages/calendar/SchedulingPageEditor";
import Jobs from "@/pages/jobs/Jobs";
import Communications from "@/pages/comms/Communications";
import CommunicationsCopilot from "@/pages/comms/CommunicationsCopilot";
import MyProfile from "@/pages/MyProfile";
import EmailCallback from "@/pages/settings/EmailCallback";
import JobDetail from "@/pages/jobs/JobDetail";
import { PublicCareers, PublicJobApply } from "@/pages/careers/Careers";
import TaskSettings from "@/pages/admin/TaskSettings";
import AdminLayout from "@/pages/admin/AdminLayout";
import Pipelines from "@/pages/admin/Pipelines";
import StatusesSources from "@/pages/admin/StatusesSources";
import DateReview from "@/pages/admin/DateReview";
import DataImport from "@/pages/admin/DataImport";
import CrmConfig from "@/pages/admin/CrmConfig";
import Lists from "@/pages/people/Lists";
import Agencies from "@/pages/agencies/Agencies";
import AgencyDetail from "@/pages/agencies/AgencyDetail";
import DistrictPhonebook from "@/pages/agencies/DistrictPhonebook";
import AccountSettings from "@/pages/admin/AccountSettings";
import Organizations from "@/pages/admin/Organizations";
import Users from "@/pages/admin/Users";
import RolesPermissions from "@/pages/admin/RolesPermissions";
import { PersonClassifications, CandidateTypes } from "@/pages/admin/ConfigManager";
import CustomFields from "@/pages/admin/CustomFields";
import RecordLayouts from "@/pages/admin/RecordLayouts";
import Branding from "@/pages/admin/Branding";
import FeatureSettings from "@/pages/admin/FeatureSettings";
import AuditLog from "@/pages/admin/AuditLog";
import RecycleBin from "@/pages/admin/RecycleBin";
import AttachmentReview from "@/pages/admin/AttachmentReview";
import OrganizationAssignment from "@/pages/admin/OrganizationAssignment";
import PlatformAccounts from "@/pages/superadmin/PlatformAccounts";
import PlatformAccountDetail from "@/pages/superadmin/PlatformAccountDetail";
import PublicHome from "@/pages/public/PublicHome";
import PublicSchedule from "@/pages/public/PublicSchedule";
import PublicScheduleManage from "@/pages/public/PublicScheduleManage";
import PublicGroupSchedule from "@/pages/public/PublicGroupSchedule";
import GroupSessions from "@/pages/calendar/GroupSessions";
import Register from "@/pages/public/Register";
import PublicApply from "@/pages/public/PublicApply";
import UnderReview from "@/pages/public/UnderReview";
import CandidatePortal from "@/pages/portal/CandidatePortal";
import Registrations from "@/pages/admin/Registrations";
import Territories from "@/pages/admin/Territories";
import KeyContacts from "@/pages/admin/KeyContacts";
import Programs from "@/pages/admin/Programs";
import TransferRequests from "@/pages/admin/TransferRequests";
import Backups from "@/pages/admin/Backups";
import InstallHelp from "@/pages/public/InstallHelp";
import BackgroundChecks from "@/pages/admin/BackgroundChecks";
import RecruitingGoals from "@/pages/admin/RecruitingGoals";
import SupportSettings from "@/pages/admin/SupportSettings";
import StandardMeetings from "@/pages/admin/StandardMeetings";
import OutsideRecruiters from "@/pages/admin/OutsideRecruiters";
import AdminCenter from "@/pages/admin/AdminCenter";
import AdminPages from "@/pages/admin/AdminPages";
import AddressMigrationReport from "@/pages/admin/AddressMigrationReport";
import AdminPeople from "@/pages/admin/AdminPeople";
import AdminPageEdit from "@/pages/admin/AdminPageEdit";
import ManagedPage from "@/pages/ManagedPage";
import { useAdminCan } from "@/pages/admin/adminCards";
import FilesAssets from "@/pages/admin/FilesAssets";
import SharedFile from "@/pages/SharedFile";
import DocumentPreview from "@/pages/DocumentPreview";
import BonusSchedules from "@/pages/admin/BonusSchedules";
import InterviewImport from "@/pages/admin/InterviewImport";
import PrimeScores from "@/pages/admin/PrimeScores";
import AcquisitionContentEditor from "@/pages/admin/AcquisitionContentEditor";
import RecruitingPartners from "@/pages/admin/RecruitingPartners";
import PartnerWorkspace from "@/pages/partner/PartnerWorkspace";
import PathwaysToOwnership from "@/pages/pathways/PathwaysToOwnership";
import OwnershipDeckAdmin from "@/pages/admin/OwnershipDeckAdmin";
import PresentOwnership from "@/pages/ownership/PresentOwnership";
import DuplicateFinder from "@/pages/admin/DuplicateFinder";
import TaskCleanup from "@/pages/admin/TaskCleanup";
import DemoCleanup from "@/pages/admin/DemoCleanup";
import ProgramMismatch from "@/pages/admin/ProgramMismatch";
import ProgressReconcile from "@/pages/admin/ProgressReconcile";
import CandidateReview from "@/pages/admin/CandidateReview";
import FormsAdmin from "@/pages/admin/FormsAdmin";
import FormBuilder from "@/pages/admin/FormBuilder";
import AnswerLibrary from "@/pages/admin/AnswerLibrary";
import AiUsageCost from "@/pages/admin/AiUsageCost";
import FormResponses from "@/pages/admin/FormResponses";
import FormAssignments from "@/pages/admin/FormAssignments";
import MyReviews from "@/pages/reviews/MyReviews";
import ReviewRunner from "@/pages/reviews/ReviewRunner";
import MeetingPrep from "@/pages/reviews/MeetingPrep";
import FormCorrections from "@/pages/admin/FormCorrections";
import GroupSchedulingAdmin from "@/pages/admin/GroupSchedulingAdmin";
import PublicGroupBooking from "@/pages/scheduling/PublicGroupBooking";
import ReferralPartners from "@/pages/admin/ReferralPartners";
import NeedsReviewQueue from "@/pages/admin/NeedsReviewQueue";
import ScheduleInterview from "@/pages/recruiting/ScheduleInterview";
import { InstallPrompt } from "@/components/InstallPrompt";

const Loader = () => <div className="min-h-screen flex items-center justify-center"><PageLoader label="Loading Top-Recruit" /></div>;

function userDest(user, ctx) {
  const st = user?.account_state;
  if (st && ["registration_submitted", "email_verification_pending", "area_review_pending", "declined", "inactive"].includes(st)) return "review";
  const roles = new Set((ctx?.memberships || []).map((m) => m.role));
  if (roles.size === 0 && st === "applicant") return "applicant";
  if (roles.size > 0 && [...roles].every((r) => r === "candidate")) return "portal";
  if (st === "approved" && (!ctx || (ctx.memberships || []).length === 0)) return "review";
  return "internal";
}

function Protected({ children }) {
  const { user, ctx } = useAuth();
  const loc = useLocation();
  if (user === null || (user && ctx === null)) return <Loader />;
  if (!user) {
    const back = loc.pathname + loc.search;
    return <Navigate to={back && back !== "/" ? `/login?return=${encodeURIComponent(back)}` : "/login"} replace />;
  }
  const dest = userDest(user, ctx);
  if (dest === "applicant") return <Navigate to="/applicant" replace />;
  if (dest === "portal") return <Navigate to="/portal" replace />;
  if (dest === "review") return <Navigate to="/under-review" replace />;
  return <AppShell>{children}</AppShell>;
}

function SignedIn({ children }) {
  const { user } = useAuth();
  const loc = useLocation();
  if (user === null) return <Loader />;
  if (!user) return <Navigate to={`/login?return=${encodeURIComponent(loc.pathname + loc.search)}`} replace />;
  return children;
}

function Root() {
  const { user, ctx } = useAuth();
  if (user === null) return <Loader />;
  if (!user) return <PublicHome />;
  if (ctx === null) return <Loader />;
  const dest = userDest(user, ctx);
  if (dest === "applicant") return <Navigate to="/applicant" replace />;
  if (dest === "portal") return <Navigate to="/portal" replace />;
  if (dest === "review") return <Navigate to="/under-review" replace />;
  return <AppShell><Home /></AppShell>;
}

function PortalRoute() {
  const { user, ctx } = useAuth();
  if (user === null || (user && ctx === null)) return <Loader />;
  if (!user) return <Navigate to="/login" replace />;
  const dest = userDest(user, ctx);
  if (dest === "review") return <Navigate to="/under-review" replace />;
  if (dest === "internal") return <Navigate to="/" replace />;
  if (dest === "applicant") return <Navigate to="/applicant" replace />;
  return <CandidatePortal />;
}

function UnderReviewRoute() {
  const { user, ctx } = useAuth();
  if (user === null) return <Loader />;
  if (!user) return <Navigate to="/login" replace />;
  if (ctx) {
    const dest = userDest(user, ctx);
    if (dest === "portal") return <Navigate to="/portal" replace />;
    if (dest === "internal") return <Navigate to="/" replace />;
    if (dest === "applicant") return <Navigate to="/applicant" replace />;
  }
  return <UnderReview />;
}

function OldAgencyJobLink() {
  const { code } = useParams();
  return <Navigate to={`/j/${code}`} replace />;
}

// Admin surfaces never silently redirect to Home — an authenticated-but-unauthorized user
// sees a professional Access Denied experience inside the app shell instead.
function AdminGuard({ children }) {
  const { isAdmin, user, ctx } = useAuth();
  if (user === null || (user && ctx === null)) return <PageLoader />;
  if (!isAdmin) return <AccessDenied />;
  return children;
}

function ManagerGuard({ children }) {
  const { isManager, user, ctx } = useAuth();
  if (user === null || (user && ctx === null)) return <PageLoader />;
  if (!isManager) return <AccessDenied />;
  return children;
}

function AdminHubGuard({ children }) {
  // The Admin Center landing page is open to anyone holding at least one administrative function.
  const { user, ctx } = useAuth();
  const { hasAdmin } = useAdminCan();
  if (user === null || (user && ctx === null)) return <PageLoader />;
  if (!hasAdmin) return <AccessDenied title="You don't have permission to manage this area." />;
  return children;
}

function AdminCenterGuard({ children }) {
  // Admin Center is reachable by any administrative role; the launcher + AdminLayout filter items by scope.
  const { isAdmin, isManager, isGroupAdmin, isSuper, user, ctx } = useAuth();
  if (user === null || (user && ctx === null)) return <PageLoader />;
  if (!(isAdmin || isManager || isGroupAdmin || isSuper)) return <AccessDenied />;
  return children;
}

function HelpAdminGuard({ children }) {
  // Help content management is admin-only (matches backend _can_manage): super / account admin / group admin.
  const { isAdmin, isGroupAdmin, isSuper, user, ctx } = useAuth();
  if (user === null || (user && ctx === null)) return <PageLoader />;
  if (!(isAdmin || isGroupAdmin || isSuper)) return <AccessDenied />;
  return children;
}

function KCGuard({ children }) {
  // Knowledge Center library: Support Staff and higher internal roles (server enforces scope).
  const { user, ctx } = useAuth();
  const { can } = useAdminCan();
  if (user === null || (user && ctx === null)) return <PageLoader />;
  if (!can.kc) return <AccessDenied />;
  return children;
}

function KnowledgeGuard({ children }) {
  // Knowledge Center management: super / account admin / group admin (matches backend _can_read_any).
  const { isAdmin, isGroupAdmin, isSuper, user, ctx } = useAuth();
  if (user === null || (user && ctx === null)) return <PageLoader />;
  if (!(isAdmin || isGroupAdmin || isSuper)) return <AccessDenied />;
  return children;
}

function FormsGuard({ children }) {
  // Forms: admins, Group Admins, and District Support Staff (own forms only; enforced server-side).
  const { isAdmin, isGroupAdmin, isSuper, user, ctx, roles } = useAuth();
  if (user === null || (user && ctx === null)) return <PageLoader />;
  if (!(isAdmin || isGroupAdmin || isSuper || roles?.has("recruiting_support"))) return <AccessDenied />;
  return children;
}

function UsersAdminGuard({ children }) {
  const { canViewUsers, user, ctx } = useAuth();
  if (user === null || (user && ctx === null)) return <PageLoader />;
  if (!canViewUsers) return <AccessDenied />;
  return children;
}

function PathwaysGuard({ children }) {
  const { isReferralPartner, isOutside, isSuper, isAdmin, isGroupAdmin, roles, user, ctx } = useAuth();
  if (user === null || (user && ctx === null)) return <PageLoader />;
  const ok = isReferralPartner || isOutside || isSuper || isAdmin || isGroupAdmin || roles?.has?.("external_recruiting_manager") || roles?.has?.("external_recruiter_admin");
  return ok ? children : <Navigate to="/" replace />;
}

function SuperGuard({ children }) {
  const { isSuper, user, ctx } = useAuth();
  if (user === null || (user && ctx === null)) return <PageLoader />;
  if (!isSuper) return <Navigate to="/" replace />;
  return children;
}

function SchedulingGuard({ children }) {
  const { canSchedule, user, ctx } = useAuth();
  if (user === null || (user && ctx === null)) return <PageLoader />;
  if (!canSchedule) return <AccessDenied />;
  return children;
}

function RecruitingGuard({ children }) {
  const { canRecruiting, user, ctx } = useAuth();
  if (user === null || (user && ctx === null)) return <PageLoader />;
  if (!canRecruiting) return <Navigate to="/" replace />;
  return children;
}

function GoalsGuard({ children }) {  const { isAdmin, isManager, isGroupAdmin, canRecruiting, user, ctx } = useAuth();
  if (user === null || (user && ctx === null)) return <PageLoader />;
  if (!(isAdmin || isManager || isGroupAdmin || canRecruiting)) return <AccessDenied />;
  return children;
}

function ReferralGuard({ children }) {
  const { isReferralPartner, isOutside, isSuper, user, ctx } = useAuth();
  if (user === null || (user && ctx === null)) return <PageLoader />;
  if (!(isReferralPartner || isOutside || isSuper)) return <Navigate to="/" replace />;
  return children;
}

function MyTeamRoute() {
  // Agents / Agent Staff get their Agency team; internal staff keep the candidate Support Team page.
  const { isOfficeUser } = useAuth();
  return isOfficeUser ? <AgentMyTeam /> : <MyTeam />;
}

function InternalGuard({ children }) {
  // Keeps portal-style roles (Referral Partner, Outside Recruiter) out of the internal People/HR UIs.
  const { isReferralPartner, isOutside, user, ctx } = useAuth();
  if (user === null || (user && ctx === null)) return <PageLoader />;
  if (isReferralPartner || isOutside) return <Navigate to="/" replace />;
  return children;
}

function SupportSettingsGuard({ children }) {
  const { isAdmin, isSuper, isGroupAdmin, user, ctx } = useAuth();
  if (user === null || (user && ctx === null)) return <PageLoader />;
  if (!(isAdmin || isSuper || isGroupAdmin)) return <AccessDenied />;
  return children;
}

function PublicOnly({ children }) {
  const { user } = useAuth();
  if (user === null) return <Loader />;
  if (user) return <Navigate to="/" replace />;
  return children;
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <ScrollToTop />
        <Routes>
          <Route path="/login" element={<PublicOnly><Login /></PublicOnly>} />
          <Route path="/register" element={<PublicOnly><Register /></PublicOnly>} />
          <Route path="/apply/:slug" element={<PublicApply />} />
          <Route path="/under-review" element={<UnderReviewRoute />} />
          <Route path="/portal" element={<PortalRoute />} />
          <Route path="/forgot-password" element={<PublicOnly><ForgotPassword /></PublicOnly>} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/accept-invitation" element={<AcceptInvitation />} />
          <Route path="/install-help" element={<InstallHelp />} />
          <Route path="/verify-email" element={<VerifyEmail />} />
          <Route path="/careers" element={<PublicCareers />} />
          <Route path="/careers/:jobId" element={<PublicJobApply />} />
          <Route path="/schedule/manage/:token" element={<PublicScheduleManage />} />
          <Route path="/schedule/:slug/group/:token" element={<PublicGroupSchedule />} />
          <Route path="/schedule/:slug" element={<PublicSchedule />} />
          <Route path="/book/:slug" element={<PublicGroupBooking />} />
          <Route path="/apply/agency/:code" element={<OldAgencyJobLink />} />
          <Route path="/j/:code" element={<JobLanding />} />
          <Route path="/applicant" element={<SignedIn><ApplicantPortal /></SignedIn>} />

          <Route path="/" element={<Root />} />
          <Route path="/security" element={<Protected><Security /></Protected>} />
          <Route path="/help" element={<Protected><HelpCenter /></Protected>} />
          <Route path="/help/article/:slug" element={<Protected><HelpArticle /></Protected>} />
          <Route path="/support/my-tickets" element={<Protected><MyTickets /></Protected>} />
          <Route path="/support/tickets" element={<Protected><SupportTicketsQueue /></Protected>} />
          <Route path="/people" element={<Protected><InternalGuard><PeopleList /></InternalGuard></Protected>} />
          <Route path="/my-team" element={<Protected><MyTeamRoute /></Protected>} />
          <Route path="/agency/leads" element={<Protected><AgencyLeads /></Protected>} />
          <Route path="/agency/hiring" element={<Protected><AgencyHiring /></Protected>} />
          <Route path="/my-roadmap" element={<Protected><MyRoadmapPage /></Protected>} />
          <Route path="/idea-share" element={<Protected><IdeaShare /></Protected>} />
          <Route path="/directory" element={<Protected><Directory /></Protected>} />
          <Route path="/people/deleted" element={<Protected><InternalGuard><DeletedPeople /></InternalGuard></Protected>} />
          <Route path="/people/detail/:id" element={<Protected><InternalGuard><PersonDetail /></InternalGuard></Protected>} />
          <Route path="/people/:classification" element={<Protected><InternalGuard><PeopleList /></InternalGuard></Protected>} />

          <Route path="/recruiting" element={<Protected><RecruitingGuard><Recruiting /></RecruitingGuard></Protected>} />
          <Route path="/recruiting/new-leads" element={<Protected><RecruitingGuard><NewLeads /></RecruitingGuard></Protected>} />
          <Route path="/recruiting/my-dashboard" element={<Protected><RecruitingGuard><MyDashboard /></RecruitingGuard></Protected>} />
          <Route path="/schedule-interview" element={<Protected><RecruitingGuard><ScheduleInterview /></RecruitingGuard></Protected>} />
          <Route path="/my-candidates" element={<Protected><RecruitingGuard><MyReferrals /></RecruitingGuard></Protected>} />
          <Route path="/people/lists" element={<Protected><RecruitingGuard><Lists /></RecruitingGuard></Protected>} />
          <Route path="/agencies" element={<Protected><RecruitingGuard><Agencies /></RecruitingGuard></Protected>} />
          <Route path="/agencies/districts/:orgId" element={<Protected><RecruitingGuard><DistrictPhonebook /></RecruitingGuard></Protected>} />
          <Route path="/agencies/:id" element={<Protected><RecruitingGuard><AgencyDetail /></RecruitingGuard></Protected>} />
          <Route path="/programs/:slug" element={<Protected><RecruitingGuard><ProgramPage /></RecruitingGuard></Protected>} />
          <Route path="/my-proteges" element={<Protected><MyProteges /></Protected>} />
          <Route path="/agent-dashboard" element={<Protected><AgentHome preview /></Protected>} />
          <Route path="/training" element={<Protected><TrainingLibrary /></Protected>} />
          <Route path="/training/:id" element={<Protected><TrainingItem /></Protected>} />
          <Route path="/admin/address-migration" element={<Protected><AddressMigrationReport /></Protected>} />
          <Route path="/admin/people" element={<Protected><AdminPeople /></Protected>} />
          <Route path="/library" element={<Protected><ContentLibrary /></Protected>} />
          <Route path="/library/:id" element={<Protected><ContentReader /></Protected>} />
          <Route path="/events" element={<Protected><EventsTrainingPage /></Protected>} />
          <Route path="/events/manage" element={<Protected><EventsManagement /></Protected>} />
          <Route path="/events/:id" element={<Protected><EventsTrainingPage /></Protected>} />
          <Route path="/recruiting/opportunity/:id" element={<Protected><RecruitingGuard><OpportunityDetail /></RecruitingGuard></Protected>} />
          <Route path="/referrals" element={<Protected><ReferralGuard><ReferralDashboard /></ReferralGuard></Protected>} />
          <Route path="/referrals/add" element={<Protected><ReferralGuard><AddLead /></ReferralGuard></Protected>} />
          <Route path="/referrals/list" element={<Protected><ReferralGuard><MyReferralsList /></ReferralGuard></Protected>} />
          <Route path="/referrals/meetings" element={<Protected><ReferralGuard><MyReferralsList meet /></ReferralGuard></Protected>} />
          <Route path="/referrals/invite" element={<Protected><ReferralGuard><InviteCandidate /></ReferralGuard></Protected>} />
          <Route path="/referrals/activity" element={<Protected><ReferralGuard><MyRecruitingActivity /></ReferralGuard></Protected>} />
          <Route path="/pathways-to-ownership" element={<Protected><PathwaysGuard><PathwaysToOwnership /></PathwaysGuard></Protected>} />
          <Route path="/referrals/knowledge" element={<Protected><ReferralGuard><PartnerKnowledge /></ReferralGuard></Protected>} />
          <Route path="/referrals/:personId" element={<Protected><ReferralGuard><ReferralDetail /></ReferralGuard></Protected>} />
          <Route path="/tasks" element={<Protected><Tasks /></Protected>} />
          <Route path="/tasks/detail/:id" element={<Protected><TaskDetail /></Protected>} />
          <Route path="/projects" element={<Protected><Projects /></Protected>} />
          <Route path="/projects/:id" element={<Protected><ProjectDetail /></Protected>} />
          <Route path="/calendar" element={<Protected><Calendar /></Protected>} />
          <Route path="/meetings/book" element={<Protected><BookMeeting /></Protected>} />
          <Route path="/admin/booking-access" element={<Protected><BookingAccessAdmin /></Protected>} />
          <Route path="/reports/booker-activity" element={<Protected><BookerActivityReport /></Protected>} />
          <Route path="/reports/recruiter-activity/reconciliation" element={<Protected><RecruiterActivityReconciliation /></Protected>} />
          <Route path="/reports/monday-brief" element={<Navigate to="/activity-report" replace />} />
          <Route path="/activity-report" element={<Protected><ActivityReport /></Protected>} />
          <Route path="/activity-report/records" element={<Protected><ActivityRecords /></Protected>} />
          <Route path="/reports/metric-list" element={<Protected><MetricList /></Protected>} />
          <Route path="/calendar/document-meeting" element={<Protected><RecruitingGuard><DocumentMeeting /></RecruitingGuard></Protected>} />
          <Route path="/calendar/scheduling-pages" element={<Protected><SchedulingGuard><SchedulingPages /></SchedulingGuard></Protected>} />
          <Route path="/calendar/scheduling-pages/:id" element={<Protected><SchedulingGuard><SchedulingPageEditor /></SchedulingGuard></Protected>} />
          <Route path="/calendar/scheduling-pages/:id/groups" element={<Protected><RecruitingGuard><GroupSessions /></RecruitingGuard></Protected>} />
          <Route path="/jobs" element={<Protected><RecruitingGuard><Jobs /></RecruitingGuard></Protected>} />
          <Route path="/jobs/:id" element={<Protected><RecruitingGuard><JobDetail /></RecruitingGuard></Protected>} />
          <Route path="/communications" element={<Protected><RecruitingGuard><Communications /></RecruitingGuard></Protected>} />
          <Route path="/communications/copilot" element={<Protected><RecruitingGuard><CommunicationsCopilot /></RecruitingGuard></Protected>} />
          <Route path="/profile" element={<Protected><MyProfile /></Protected>} />
          <Route path="/settings/email" element={<Navigate to="/profile?tab=connected" replace />} />
          <Route path="/settings/email/callback" element={<Protected><EmailCallback /></Protected>} />

          <Route path="/admin/registrations" element={<Protected><ManagerGuard><AdminLayout /></ManagerGuard></Protected>}>
            <Route index element={<Registrations />} />
          </Route>
          <Route path="/admin/territories" element={<Protected><ManagerGuard><AdminLayout /></ManagerGuard></Protected>}>
            <Route index element={<Territories />} />
          </Route>
          <Route path="/admin/key-contacts" element={<Protected><ManagerGuard><AdminLayout /></ManagerGuard></Protected>}>
            <Route index element={<KeyContacts />} />
          </Route>
          <Route path="/admin/interview-import" element={<Protected><AdminCenterGuard><AdminLayout /></AdminCenterGuard></Protected>}>
            <Route index element={<InterviewImport />} />
          </Route>
          <Route path="/admin/prime-scores" element={<Protected><AdminCenterGuard><AdminLayout /></AdminCenterGuard></Protected>}>
            <Route index element={<PrimeScores />} />
          </Route>
          <Route path="/admin/acquisition-journey" element={<Protected><AdminCenterGuard><AdminLayout /></AdminCenterGuard></Protected>}>
            <Route index element={<AcquisitionContentEditor />} />
          </Route>
          <Route path="/admin/recruiting-partners" element={<Protected><AdminCenterGuard><AdminLayout /></AdminCenterGuard></Protected>}>
            <Route index element={<RecruitingPartners />} />
          </Route>
          <Route path="/partner" element={<Protected><PartnerWorkspace /></Protected>} />
          <Route path="/admin/duplicates" element={<Protected><AdminCenterGuard><AdminLayout /></AdminCenterGuard></Protected>}>
            <Route index element={<DuplicateFinder />} />
          </Route>
          <Route path="/admin/task-cleanup" element={<Protected><AdminCenterGuard><AdminLayout /></AdminCenterGuard></Protected>}>
            <Route index element={<TaskCleanup />} />
          </Route>
          <Route path="/admin/demo-cleanup" element={<Protected><AdminCenterGuard><AdminLayout /></AdminCenterGuard></Protected>}>
            <Route index element={<DemoCleanup />} />
          </Route>
          <Route path="/admin/program-mismatch" element={<Protected><AdminCenterGuard><AdminLayout /></AdminCenterGuard></Protected>}>
            <Route index element={<ProgramMismatch />} />
          </Route>
          <Route path="/admin/candidate-review" element={<Protected><AdminCenterGuard><AdminLayout /></AdminCenterGuard></Protected>}>
            <Route index element={<CandidateReview />} />
          </Route>
          <Route path="/admin/progress-reconcile" element={<Protected><AdminCenterGuard><AdminLayout /></AdminCenterGuard></Protected>}>
            <Route index element={<ProgressReconcile />} />
          </Route>
          <Route path="/admin/activity-report-settings" element={<Protected><AdminCenterGuard><AdminLayout /></AdminCenterGuard></Protected>}>
            <Route index element={<ActivityReportSettings />} />
          </Route>
          <Route path="/admin/bonus-schedules" element={<Protected><AdminCenterGuard><AdminLayout /></AdminCenterGuard></Protected>}>
            <Route index element={<BonusSchedules />} />
          </Route>
          <Route path="/documents/preview" element={<SignedIn><DocumentPreview /></SignedIn>} />
          <Route path="/files/open/:shareId" element={<Protected><SharedFile /></Protected>} />
          <Route path="/admin/files" element={<Protected><AdminGuard><AdminLayout /></AdminGuard></Protected>}>
            <Route index element={<FilesAssets />} />
          </Route>
          <Route path="/admin/programs" element={<Protected><AdminCenterGuard><AdminLayout /></AdminCenterGuard></Protected>}>
            <Route index element={<Programs />} />
          </Route>
          <Route path="/admin/transfer-requests" element={<Protected><AdminCenterGuard><AdminLayout /></AdminCenterGuard></Protected>}>
            <Route index element={<TransferRequests />} />
          </Route>
          <Route path="/admin/backups" element={<Protected><AdminCenterGuard><AdminLayout /></AdminCenterGuard></Protected>}>
            <Route index element={<Backups />} />
          </Route>
          <Route path="/admin/support-team" element={<Protected><AdminCenterGuard><AdminLayout /></AdminCenterGuard></Protected>}>
            <Route index element={<SupportTeamEditor />} />
          </Route>
          <Route path="/admin/standard-meetings" element={<Protected><ManagerGuard><AdminLayout /></ManagerGuard></Protected>}>
            <Route index element={<StandardMeetings />} />
          </Route>
          <Route path="/admin/outside-recruiters" element={<Protected><ManagerGuard><AdminLayout /></ManagerGuard></Protected>}>
            <Route index element={<OutsideRecruiters />} />
          </Route>
          <Route path="/admin/agent-content" element={<Protected><AdminCenterGuard><AdminLayout /></AdminCenterGuard></Protected>}>
            <Route index element={<AgentContentAdmin />} />
          </Route>
          <Route path="/admin/center" element={<Protected><AdminHubGuard><AdminLayout /></AdminHubGuard></Protected>}>
            <Route index element={<AdminCenter />} />
          </Route>
          <Route path="/admin/referral-reviews" element={<Protected><AdminCenterGuard><AdminLayout /></AdminCenterGuard></Protected>}>
            <Route index element={<NeedsReviewQueue />} />
          </Route>
          <Route path="/admin/referral-partners" element={<Protected><AdminCenterGuard><AdminLayout /></AdminCenterGuard></Protected>}>
            <Route index element={<ReferralPartners />} />
          </Route>
          <Route path="/admin/pages" element={<Protected><SuperGuard><AdminLayout /></SuperGuard></Protected>}>
            <Route index element={<AdminPages />} />
            <Route path=":id" element={<AdminPageEdit />} />
          </Route>
          <Route path="/admin/agency-ownership" element={<Protected><SuperGuard><AdminLayout /></SuperGuard></Protected>}>
            <Route index element={<OwnershipDeckAdmin />} />
          </Route>
          <Route path="/present/agency-ownership/:pid" element={<Protected><InternalGuard><PresentOwnership /></InternalGuard></Protected>} />
          <Route path="/pages/:slug" element={<Protected><ManagedPage /></Protected>} />
          <Route path="/admin/help" element={<Protected><HelpAdminGuard><AdminLayout /></HelpAdminGuard></Protected>}>
            <Route index element={<HelpManager />} />
          </Route>
          <Route path="/admin/knowledge" element={<Protected><KCGuard><AdminLayout /></KCGuard></Protected>}>
            <Route index element={<KnowledgeLibrary />} />
          </Route>
          <Route path="/admin/knowledge/advanced" element={<Protected><KnowledgeGuard><AdminLayout /></KnowledgeGuard></Protected>}>
            <Route index element={<KnowledgeCenter />} />
          </Route>
          <Route path="/admin/knowledge/import" element={<Protected><KnowledgeGuard><AdminLayout /></KnowledgeGuard></Protected>}>
            <Route index element={<KnowledgeImport />} />
          </Route>
          <Route path="/admin/forms" element={<Protected><FormsGuard><AdminLayout /></FormsGuard></Protected>}>
            <Route index element={<FormsAdmin />} />
            <Route path=":id" element={<FormBuilder />} />
            <Route path=":id/responses" element={<FormResponses />} />
            <Route path=":id/assignments" element={<FormAssignments />} />
            <Route path=":id/corrections" element={<FormCorrections />} />
          </Route>
          <Route path="/my-reviews" element={<Protected><MyReviews /></Protected>} />
          <Route path="/my-reviews/:id" element={<Protected><ReviewRunner /></Protected>} />
          <Route path="/meeting-prep/:id" element={<Protected><MeetingPrep /></Protected>} />
          <Route path="/admin/answer-library" element={<Protected><KnowledgeGuard><AdminLayout /></KnowledgeGuard></Protected>}>
            <Route index element={<AnswerLibrary />} />
          </Route>
          <Route path="/admin/ai-usage" element={<Protected><KnowledgeGuard><AdminLayout /></KnowledgeGuard></Protected>}>
            <Route index element={<AiUsageCost />} />
          </Route>
          <Route path="/admin/scheduling-booking" element={<Protected><AdminCenterGuard><AdminLayout /></AdminCenterGuard></Protected>}>
            <Route index element={<GroupSchedulingAdmin />} />
          </Route>
          <Route path="/admin/background-checks" element={<Protected><ManagerGuard><AdminLayout /></ManagerGuard></Protected>}>
            <Route index element={<BackgroundChecks />} />
          </Route>
          <Route path="/admin/recruiting-goals" element={<Protected><GoalsGuard><AdminLayout /></GoalsGuard></Protected>}>
            <Route index element={<RecruitingGoals />} />
          </Route>
          <Route path="/admin/support-settings" element={<Protected><SupportSettingsGuard><AdminLayout /></SupportSettingsGuard></Protected>}>
            <Route index element={<SupportSettings />} />
          </Route>
          <Route path="/admin/users" element={<Protected><UsersAdminGuard><AdminLayout /></UsersAdminGuard></Protected>}>
            <Route index element={<Users />} />
          </Route>
          <Route path="/admin/date-review" element={<Protected><AdminCenterGuard><AdminLayout /></AdminCenterGuard></Protected>}>
            <Route index element={<DateReview />} />
          </Route>
          <Route path="/admin/statuses-sources" element={<Protected><AdminCenterGuard><AdminLayout /></AdminCenterGuard></Protected>}>
            <Route index element={<StatusesSources />} />
          </Route>
          <Route path="/admin" element={<Protected><AdminGuard><AdminLayout /></AdminGuard></Protected>}>
            <Route index element={<AccountSettings />} />
            <Route path="organizations" element={<Organizations />} />
            <Route path="roles" element={<RolesPermissions />} />
            <Route path="classifications" element={<PersonClassifications />} />
            <Route path="candidate-types" element={<CandidateTypes />} />
            <Route path="pipelines" element={<Pipelines />} />
            <Route path="imports" element={<DataImport />} />
            <Route path="crm-config" element={<CrmConfig />} />
            <Route path="task-settings" element={<TaskSettings />} />
            <Route path="custom-fields" element={<CustomFields />} />
            <Route path="record-layouts" element={<RecordLayouts />} />
            <Route path="branding" element={<Branding />} />
            <Route path="features" element={<FeatureSettings />} />
            <Route path="audit-log" element={<AuditLog />} />
            <Route path="attachment-review" element={<AttachmentReview />} />
            <Route path="org-assignment" element={<OrganizationAssignment />} />
            <Route path="recycle-bin" element={<RecycleBin />} />
          </Route>

          <Route path="/super-admin" element={<Protected><SuperGuard><PlatformAccounts /></SuperGuard></Protected>} />
          <Route path="/super-admin/accounts/:id" element={<Protected><SuperGuard><PlatformAccountDetail /></SuperGuard></Protected>} />

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        <InstallPrompt />
      </BrowserRouter>
      <Toaster position="top-right" richColors />
    </AuthProvider>
  );
}
